import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';

// True inside the Android app (Capacitor), false on the website.
export const isNativeApp = Capacitor.isNativePlatform();

// Public website origin for links people share. In the app the page itself is served from
// https://localhost, so shared links must point at the real site instead.
export function siteOrigin(): string {
  return isNativeApp ? process.env.REACT_APP_SITE_URL || 'https://cheers2night.com' : window.location.origin;
}

// Opens Android's share sheet. Returns false on the website, so callers can fall back.
export async function nativeShare(options: { title?: string; text?: string; url?: string }): Promise<boolean> {
  if (!isNativeApp) return false;
  try {
    await Share.share({ ...options, dialogTitle: options.title });
  } catch {
    /* closed by the user */
  }
  return true;
}

const toBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

// Saves a generated file (CSV, image). The website downloads it; the app, whose WebView can't
// download blobs, writes it to the cache and opens the share sheet (Save to Files/Drive, WhatsApp, ...).
export async function saveFile(filename: string, blob: Blob, title?: string): Promise<void> {
  if (!isNativeApp) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  const { uri } = await Filesystem.writeFile({ path: filename, data: await toBase64(blob), directory: Directory.Cache });
  try {
    await Share.share({ title: title ?? filename, files: [uri], dialogTitle: title ?? filename });
  } catch {
    /* closed by the user */
  }
}

// App-only behaviour, mounted once inside the router: hardware back button, opening
// cheers2night.com links (e.g. a shared invite) inside the app, and hiding the splash screen.
export function NativeBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp) return;
    SplashScreen.hide().catch(() => undefined);

    const back = App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else App.exitApp();
    });
    const open = App.addListener('appUrlOpen', ({ url }) => {
      try {
        const u = new URL(url);
        if (u.host === new URL(siteOrigin()).host) navigate(`${u.pathname}${u.search}`);
      } catch {
        /* not a URL we handle */
      }
    });
    return () => {
      back.then((h) => h.remove());
      open.then((h) => h.remove());
    };
  }, [navigate]);

  return null;
}
